import { CHAIN_IDS, ChainName, NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import { machProtocolAPI } from '@src/libs/machProtocol'
import { v1Api } from '@src/libs/v1Api'
import { IAllBalancesResponse, IBalanceResponse } from '@src/types/balance'
import {
    IAllAssetsResponse,
    IAssestByChainNameResponse,
    IAssetDataResponse,
    IPricesResponse,
} from '@src/types/machprotocol.api'
import { getChecksumAddress } from '@src/utils/address'
import { isBitcoinAddress } from '@src/utils/bitcoin/address'
import { isFeatureEnabled } from '@src/utils/environment'
import { apiLogger as logger } from '@src/utils/logger'

export const apiGetAllUSDPrices = async (): Promise<IPricesResponse[]> => {
    const { data } = await machProtocolAPI.get<IPricesResponse[]>('/prices')
    return data
}

/**
 * V2 API Token Model interface matching the new API structure
 */
interface V2TokenModel {
    token_id: string
    chain_id: string | number
    address: string
    symbol: string
    name: string
    decimals: number
    image: string
    price: number
    market_cap: number
    volume_24h: number
    high_24h: number
    low_24h: number
    price_change_24h: number
    price_change_percentage_24h: number
    last_updated: string
}

type V2ChainTokenModel = Record<string, V2TokenModel>
type V2TokensResponse = Record<string, V2ChainTokenModel>

/**
 * Transform V2 API tokens response to the legacy format
 */
const transformV2TokensResponse = (tokensData: V2TokensResponse): IAllAssetsResponse => {
    logger.info('Transforming V2 API tokens data to legacy format')

    const result: IAllAssetsResponse = {}

    // Process each chain's tokens
    Object.entries(tokensData).forEach(([chainId, chainTokens]) => {
        // Determine chain name based on chain ID
        // This might need to be enhanced with a more comprehensive mapping
        let chainName = ''
        for (const [name, id] of Object.entries(CHAIN_IDS)) {
            if (id.toString() === chainId) {
                chainName = name.toLowerCase()
                break
            }
        }

        if (!chainName) {
            logger.warn(`Could not find chain name for chain ID: ${chainId}`)
            return
        }

        result[chainName] = {}

        // Process each token in the chain
        Object.entries(chainTokens).forEach(([address, tokenData]) => {
            // Transform to legacy format
            const legacyData: IAssetDataResponse = {
                id: tokenData.token_id,
                symbol: tokenData.symbol,
                name: tokenData.name,
                image: tokenData.image,
                current_price: tokenData.price,
                market_cap: tokenData.market_cap,
                market_cap_rank: 0, // Not provided in V2 API
                fully_diluted_valuation: null, // Not provided in V2 API
                total_volume: tokenData.volume_24h,
                high_24h: tokenData.high_24h,
                low_24h: tokenData.low_24h,
                price_change_24h: tokenData.price_change_24h,
                price_change_percentage_24h: tokenData.price_change_percentage_24h,
                market_cap_change_24h: 0, // Not provided in V2 API
                market_cap_change_percentage_24h: 0, // Not provided in V2 API
                circulating_supply: 0, // Not provided in V2 API
                total_supply: null, // Not provided in V2 API
                max_supply: null, // Not provided in V2 API
                ath: 0, // Not provided in V2 API
                ath_change_percentage: 0, // Not provided in V2 API
                ath_date: '', // Not provided in V2 API
                atl: 0, // Not provided in V2 API
                atl_change_percentage: 0, // Not provided in V2 API
                atl_date: '', // Not provided in V2 API
                roi: null, // Not provided in V2 API
                last_updated: tokenData.last_updated,
                decimals: tokenData.decimals,
            }

            // Use 'native' as key for native tokens
            const addressKey = address === 'native' ? 'native' : address
            result[chainName][addressKey] = legacyData
        })
    })

    return result
}

/**
 * Transform V2 API single chain tokens response to the legacy format
 */
const transformV2ChainTokensResponse = (
    chainTokens: V2ChainTokenModel,
    chainId: string
): IAssestByChainNameResponse => {
    logger.info(`Transforming V2 API tokens data for chain ${chainId} to legacy format`)

    const result: IAssestByChainNameResponse = {}

    // Process each token in the chain
    Object.entries(chainTokens).forEach(([address, tokenData]) => {
        // Transform to legacy format
        const legacyData: IAssetDataResponse = {
            id: tokenData.token_id,
            symbol: tokenData.symbol,
            name: tokenData.name,
            image: tokenData.image,
            current_price: tokenData.price,
            market_cap: tokenData.market_cap,
            market_cap_rank: 0, // Not provided in V2 API
            fully_diluted_valuation: null, // Not provided in V2 API
            total_volume: tokenData.volume_24h,
            high_24h: tokenData.high_24h,
            low_24h: tokenData.low_24h,
            price_change_24h: tokenData.price_change_24h,
            price_change_percentage_24h: tokenData.price_change_percentage_24h,
            market_cap_change_24h: 0, // Not provided in V2 API
            market_cap_change_percentage_24h: 0, // Not provided in V2 API
            circulating_supply: 0, // Not provided in V2 API
            total_supply: null, // Not provided in V2 API
            max_supply: null, // Not provided in V2 API
            ath: 0, // Not provided in V2 API
            ath_change_percentage: 0, // Not provided in V2 API
            ath_date: '', // Not provided in V2 API
            atl: 0, // Not provided in V2 API
            atl_change_percentage: 0, // Not provided in V2 API
            atl_date: '', // Not provided in V2 API
            roi: null, // Not provided in V2 API
            last_updated: tokenData.last_updated,
            decimals: tokenData.decimals,
        }

        // Use 'native' as key for native tokens
        const addressKey = address === 'native' ? 'native' : address
        result[addressKey] = legacyData
    })

    return result
}

/**
 * Transform V2 API single token response to the legacy format
 */
const transformV2TokenResponse = (tokenData: V2TokenModel): IAssetDataResponse => {
    logger.info(`Transforming V2 API token data to legacy format`)

    // Transform to legacy format
    return {
        id: tokenData.token_id,
        symbol: tokenData.symbol,
        name: tokenData.name,
        image: tokenData.image,
        current_price: tokenData.price,
        market_cap: tokenData.market_cap,
        market_cap_rank: 0, // Not provided in V2 API
        fully_diluted_valuation: null, // Not provided in V2 API
        total_volume: tokenData.volume_24h,
        high_24h: tokenData.high_24h,
        low_24h: tokenData.low_24h,
        price_change_24h: tokenData.price_change_24h,
        price_change_percentage_24h: tokenData.price_change_percentage_24h,
        market_cap_change_24h: 0, // Not provided in V2 API
        market_cap_change_percentage_24h: 0, // Not provided in V2 API
        circulating_supply: 0, // Not provided in V2 API
        total_supply: null, // Not provided in V2 API
        max_supply: null, // Not provided in V2 API
        ath: 0, // Not provided in V2 API
        ath_change_percentage: 0, // Not provided in V2 API
        ath_date: '', // Not provided in V2 API
        atl: 0, // Not provided in V2 API
        atl_change_percentage: 0, // Not provided in V2 API
        atl_date: '', // Not provided in V2 API
        roi: null, // Not provided in V2 API
        last_updated: tokenData.last_updated,
        decimals: tokenData.decimals,
    }
}

export const apiGetAllTokens = async (): Promise<IAllAssetsResponse> => {
    try {
        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info('Using V2 API for token information')
            const { data } = await v1Api.get<V2TokensResponse>('/info/tokens')
            return transformV2TokensResponse(data)
        } else {
            // Fall back to the original endpoint
            logger.info('Using legacy API for token information')
            const { data } = await machProtocolAPI.get<IAllAssetsResponse>('/assets')
            return data
        }
    } catch (error) {
        logger.error('Error fetching token information:', error)
        // If the V2 API fails, fall back to the original endpoint
        logger.info('Falling back to legacy API after error')
        const { data } = await machProtocolAPI.get<IAllAssetsResponse>('/assets')
        return data
    }
}

export const apiGetAllTokensForNetwork = async (networkName: string): Promise<IAssestByChainNameResponse> => {
    try {
        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info(`Using V2 API for token information for network ${networkName}`)

            // Convert network name to chain ID
            const chainId = CHAIN_IDS[networkName as ChainName]
            if (!chainId) {
                throw new Error(`Chain ID not found for network ${networkName}`)
            }

            const { data } = await v1Api.get<V2ChainTokenModel>(`/info/tokens/${chainId}`)
            return transformV2ChainTokensResponse(data, chainId.toString())
        } else {
            // Fall back to the original endpoint
            logger.info(`Using legacy API for token information for network ${networkName}`)
            const { data } = await machProtocolAPI.get<IAssestByChainNameResponse>(`/assets/${networkName}`)
            return data
        }
    } catch (error) {
        logger.error(`Error fetching token information for network ${networkName}:`, error)
        // If the V2 API fails, fall back to the original endpoint
        logger.info('Falling back to legacy API after error')
        const { data } = await machProtocolAPI.get<IAssestByChainNameResponse>(`/assets/${networkName}`)
        return data
    }
}

export const apiGetToken = async (networkName: string, address: string): Promise<IAssetDataResponse> => {
    try {
        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info(`Using V2 API for token information for ${address} on ${networkName}`)

            // Convert network name to chain ID
            const chainId = CHAIN_IDS[networkName as ChainName]
            if (!chainId) {
                throw new Error(`Chain ID not found for network ${networkName}`)
            }

            // Transform the address for native tokens
            const tokenAddress = address === NATIVE_TOKEN_ADDRESS[chainId] ? 'native' : address

            const { data } = await v1Api.get<V2TokenModel>(`/info/tokens/${chainId}/${tokenAddress}`)
            return transformV2TokenResponse(data)
        } else {
            // Fall back to the original endpoint
            logger.info(`Using legacy API for token information for ${address} on ${networkName}`)
            const { data } = await machProtocolAPI.get<IAssetDataResponse>(`/assets/${networkName}/${address}`)
            return data
        }
    } catch (error) {
        logger.error(`Error fetching token information for ${address} on ${networkName}:`, error)
        // If the V2 API fails, fall back to the original endpoint
        logger.info('Falling back to legacy API after error')
        const { data } = await machProtocolAPI.get<IAssetDataResponse>(`/assets/${networkName}/${address}`)
        return data
    }
}

/**
 * V2 API TokenModelWithBalance interface matching the new API structure
 */
interface V2TokenModelWithBalance extends V2TokenModel {
    usd_value: number
    balance: string
}

/**
 * Transform V2 API token with balance to the legacy format
 */
const transformV2TokenBalanceResponse = (tokenData: V2TokenModelWithBalance, networkName: string): IBalanceResponse => {
    logger.info(`Transforming V2 API token balance data to legacy format for ${tokenData.address} on ${networkName}`)

    // Transform to legacy format
    return {
        address:
            tokenData.address === 'native'
                ? NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]]
                : tokenData.address,
        balance: tokenData.balance,
        chain: networkName,
        daily_percent_change: tokenData.price_change_percentage_24h || 0,
        price: tokenData.price,
        symbol: tokenData.symbol,
    }
}

/**
 * Transform V2 API token list with balances to the legacy format
 */
const transformV2TokenBalancesResponse = (
    tokensData: V2TokenModelWithBalance[],
    networkName: string
): IBalanceResponse[] => {
    logger.info(`Transforming V2 API token balances data to legacy format for network ${networkName}`)

    return tokensData.map(tokenData => transformV2TokenBalanceResponse(tokenData, networkName))
}

export const apiGetUserBalanceForToken = async (
    wallet: string | undefined,
    networkName: string,
    tokenAddress: string
): Promise<IBalanceResponse> => {
    // Skip API calls for undefined, null, or empty wallet addresses
    if (!wallet || isBitcoinAddress(wallet) || networkName === 'bitcoin') {
        return {
            address: tokenAddress,
            balance: '0',
            chain: networkName,
            daily_percent_change: 0,
            price: 0,
            symbol: '',
        }
    }

    try {
        // Normalize token address for native tokens
        const normalizedTokenAddress =
            tokenAddress === NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]] ? 'native' : tokenAddress

        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info(`Using V2 API for token balance for ${normalizedTokenAddress} on ${networkName}`)

            // Convert network name to chain ID
            const chainId = CHAIN_IDS[networkName as ChainName]
            if (!chainId) {
                throw new Error(`Chain ID not found for network ${networkName}`)
            }

            // Ensure token address is checksummed for EVM chains
            let addressToUse = normalizedTokenAddress

            // Only checksum if not a native token and chain type is EVM
            if (normalizedTokenAddress !== 'native') {
                addressToUse = getChecksumAddress(normalizedTokenAddress)
                logger.info(`Using checksummed address: ${addressToUse} for token balance request`)
            }

            // Use the specific endpoint with properly formatted token address
            const { data } = await v1Api.get<V2TokenModelWithBalance>(
                `/wallets/${wallet}/balances/${chainId}/${addressToUse}`
            )

            return transformV2TokenBalanceResponse(data, networkName)
        } else {
            // Use the original endpoint when V2 is disabled
            logger.info(`Using legacy API for token balance for ${normalizedTokenAddress} on ${networkName}`)

            const { data } = await machProtocolAPI.get<IBalanceResponse>(
                `/users/${wallet}/assets/${networkName}/${normalizedTokenAddress}`
            )

            // Replace native with nulladdress
            return normalizedTokenAddress === 'native'
                ? { ...data, address: NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]] }
                : data
        }
    } catch (error) {
        logger.error(`Error fetching token balance for ${tokenAddress} on ${networkName}:`, error)
        // Return a null balance if API fails
        return {
            address: tokenAddress,
            balance: '0',
            chain: networkName,
            daily_percent_change: 0,
            price: 0,
            symbol: '',
        }
    }
}

export const apiGetUserBalancesForChain = async (
    wallet: string | undefined,
    networkName: string
): Promise<IBalanceResponse[]> => {
    // Skip API calls for undefined, null, or empty wallet addresses or Bitcoin addresses
    if (!wallet || isBitcoinAddress(wallet) || networkName === 'bitcoin') {
        return []
    }

    try {
        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info(`Using V2 API for token balances for network ${networkName}`)

            // Convert network name to chain ID
            const chainId = CHAIN_IDS[networkName as ChainName]
            if (!chainId) {
                throw new Error(`Chain ID not found for network ${networkName}`)
            }

            // Use the specific endpoint with chain ID
            const { data } = await v1Api.get<V2TokenModelWithBalance[]>(`/wallets/${wallet}/balances/${chainId}`)

            return transformV2TokenBalancesResponse(data, networkName)
        } else {
            // Use the original endpoint when V2 is disabled
            logger.info(`Using legacy API for token balances for network ${networkName}`)

            const { data } = await machProtocolAPI.get<IBalanceResponse[]>(`/users/${wallet}/assets/${networkName}`)
            // Replace native with nulladdress
            return data.map((t: IBalanceResponse) =>
                t.address === 'native'
                    ? { ...t, address: NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]] }
                    : t
            )
        }
    } catch (error) {
        logger.error(`Error fetching token balances for network ${networkName}:`, error)
        // Return empty list if API fails
        return []
    }
}

/**
 * Deduplicate balances to avoid React key issues
 */
const deduplicateBalances = (balances: IBalanceResponse[]): IBalanceResponse[] => {
    logger.info('Deduplicating token balances to prevent duplicate React keys')

    // Use a map with a compound key to ensure uniqueness
    const uniqueBalances = new Map<string, IBalanceResponse>()

    balances.forEach(balance => {
        const key = `${balance.chain}-${balance.address}`

        // If this is a duplicate, keep the one with higher balance or price
        if (uniqueBalances.has(key)) {
            const existing = uniqueBalances.get(key)!
            const existingUSD = parseFloat(existing.balance.toString()) * (existing.price || 0)
            const currentUSD = parseFloat(balance.balance.toString()) * (balance.price || 0)

            if (currentUSD > existingUSD) {
                uniqueBalances.set(key, balance)
                logger.info(`Replaced duplicate balance for ${key} with higher value`)
            }
        } else {
            uniqueBalances.set(key, balance)
        }
    })

    return Array.from(uniqueBalances.values())
}

/**
 * Transform V2 API chain-organized balances to flat array for legacy format
 */
export const transformV2UserBalances = (
    chainBalances: Record<string, V2TokenModelWithBalance[]>
): IBalanceResponse[] => {
    logger.info('Transforming V2 API all token balances to legacy format')

    let result: IBalanceResponse[] = []
    const processedChainIds = new Set<string>() // Track already processed chain IDs

    // Process each chain's balances
    Object.entries(chainBalances).forEach(([chainId, tokensData]) => {
        // Skip if we've already processed this chain ID to avoid duplicates
        if (processedChainIds.has(chainId)) {
            logger.info(`Skipping duplicate chain ID: ${chainId}`)
            return
        }

        // Add to processed set
        processedChainIds.add(chainId)

        // Get network name from chain ID
        let networkName = ''
        for (const [name, id] of Object.entries(CHAIN_IDS)) {
            if (id.toString() === chainId) {
                networkName = name.toLowerCase()
                break
            }
        }

        if (!networkName) {
            logger.warn(`Could not find network name for chain ID: ${chainId}`)
            return
        }

        logger.info(`Transforming V2 API token balances data to legacy format for network ${networkName}`)

        // Transform tokens and add to result
        const chainBalances = transformV2TokenBalancesResponse(tokensData, networkName)
        result = result.concat(chainBalances)
    })

    return result
}

export const apiGetUserBalances = async (wallet: string | undefined): Promise<IBalanceResponse[]> => {
    // Skip API calls for undefined, null, or empty wallet addresses or Bitcoin addresses
    if (!wallet || isBitcoinAddress(wallet)) {
        return []
    }

    try {
        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info('Using V2 API for all token balances')

            // Use the main balances endpoint which is more resilient
            const { data } = await v1Api.get<Record<string, V2TokenModelWithBalance[]>>(`/wallets/${wallet}/balances`)

            // Transform the data and deduplicate to prevent duplicate React keys
            const transformedData = transformV2UserBalances(data)

            // Additional deduplication step to ensure unique balances
            return deduplicateBalances(transformedData)
        } else {
            // Fall back to the original endpoint
            logger.info('Using legacy API for all token balances')

            const { data } = await machProtocolAPI.get<IAllBalancesResponse>(`/users/${wallet}/assets`)

            // Replace native with nulladdress for all networks
            let returnData: IBalanceResponse[] = []
            for (const networkName of Object.keys(data)) {
                returnData = returnData.concat(
                    data[networkName].map((t: IBalanceResponse) =>
                        t.address === 'native'
                            ? { ...t, address: NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]] }
                            : t
                    )
                )
            }

            return returnData
        }
    } catch (error) {
        logger.error('Error fetching all token balances:', error)
        // Return empty list if API fails
        return []
    }
}
